import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Check, Loader2, Plus, Trash2, UserCog, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { followupsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const NONE = '__none__';

export const InquiryPeoplePanel = ({ inquiryId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [people, setPeople] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [followups, setFollowups] = useState({ items: [], open_count: 0, overdue_count: 0 });
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState(NONE);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [peopleRes, followupRes, directoryRes] = await Promise.all([
        followupsAPI.people(inquiryId),
        followupsAPI.list(inquiryId),
        usersAPI.directory(),
      ]);
      setPeople(peopleRes.data);
      setFollowups(followupRes.data);
      setDirectory(directoryRes.data.users || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load stakeholders');
    }
  }, [inquiryId]);

  useEffect(() => { load(); }, [load]);

  const saveAssignment = async (ownerId, watcherIds) => {
    setBusy(true);
    try {
      await followupsAPI.setAssignment(inquiryId, {
        owner_id: ownerId === NONE ? null : ownerId,
        watcher_ids: watcherIds,
      });
      toast.success('Assignment updated');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the assignment');
    } finally { setBusy(false); }
  };

  const addFollowup = async () => {
    if (!note.trim()) { toast.error('Write what needs following up'); return; }
    setBusy(true);
    try {
      await followupsAPI.create(inquiryId, {
        note, due_date: dueDate || null,
        assignee_id: assignee === NONE ? null : assignee,
      });
      setNote(''); setDueDate(''); setAssignee(NONE);
      toast.success('Follow-up added');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add the follow-up');
    } finally { setBusy(false); }
  };

  const toggleDone = async (item) => {
    try {
      await followupsAPI.update(item.id, { status: item.status === 'done' ? 'open' : 'done' });
      await load();
    } catch (error) { toast.error('Could not update the follow-up'); }
  };

  const removeFollowup = async (item) => {
    try { await followupsAPI.remove(item.id); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not remove the follow-up'); }
  };

  if (!people) {
    return <div className="h-24 animate-pulse border bg-white" data-testid="inquiry-people-loading" />;
  }

  const watcherIds = (people.watchers || []).map((watcher) => watcher.user_id);

  return (
    <section className="border bg-white p-5" data-testid="inquiry-people-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <h2 className="font-heading font-bold">Stakeholders &amp; follow-ups</h2>
        </div>
        {followups.overdue_count > 0 && (
          <Badge className="bg-rose-100 text-rose-800" data-testid="inquiry-followups-overdue">
            {followups.overdue_count} overdue
          </Badge>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <UserCog className="h-3.5 w-3.5" /> Owner
          </p>
          {isAdmin ? (
            <Select value={people.owner_id || NONE} disabled={busy}
              onValueChange={(value) => saveAssignment(value, watcherIds)}>
              <SelectTrigger data-testid="inquiry-owner-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Unassigned</SelectItem>
                {directory.map((member) => (
                  <SelectItem key={member.id} value={member.id}>{member.name} · {member.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm font-medium" data-testid="inquiry-owner-name">
              {people.owner_name || 'Unassigned'}
              <span className="ml-2 text-xs text-muted-foreground">(only an admin can reassign)</span>
            </p>
          )}
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Watchers</p>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="inquiry-watchers">
            {(people.watchers || []).map((watcher) => (
              <span key={watcher.user_id}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-600 px-3 py-1 text-xs text-white"
                data-testid={`inquiry-watcher-${watcher.user_id}`}>
                {watcher.name}
                {isAdmin && (
                  <button type="button" disabled={busy} aria-label={`Remove ${watcher.name}`}
                    onClick={() => saveAssignment(people.owner_id || NONE,
                      watcherIds.filter((id) => id !== watcher.user_id))}
                    data-testid={`inquiry-watcher-remove-${watcher.user_id}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {(people.watchers || []).length === 0 && !isAdmin && (
              <span className="text-sm text-muted-foreground">None</span>
            )}
            {isAdmin && (
              <Select value={NONE} disabled={busy}
                onValueChange={(value) => saveAssignment(people.owner_id || NONE, [...watcherIds, value])}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="inquiry-watcher-add-select">
                  <SelectValue placeholder="Add watcher" />
                </SelectTrigger>
                <SelectContent>
                  {directory
                    .filter((member) => member.id !== people.owner_id && !watcherIds.includes(member.id))
                    .map((member) => (
                      <SelectItem key={member.id} value={member.id}>{member.name} · {member.role}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 border-t pt-4">
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5" /> Follow-ups ({followups.open_count} open)
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={note} onChange={(event) => setNote(event.target.value)} className="min-w-[220px] flex-1"
            placeholder="What needs following up?" data-testid="followup-note-input" />
          <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)}
            className="w-[150px]" data-testid="followup-due-input" />
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="w-[180px]" data-testid="followup-assignee-select">
              <SelectValue placeholder="Assign to" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{people.owner_name ? `Owner (${people.owner_name})` : 'Me'}</SelectItem>
              {directory.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={addFollowup} disabled={busy} data-testid="followup-add-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        {followups.items.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="followup-list">
            {followups.items.map((item) => (
              <div key={item.id}
                className={`flex flex-wrap items-center gap-3 border-l-2 pl-3 text-sm ${
                  item.status === 'done' ? 'border-emerald-300 text-muted-foreground'
                    : item.is_overdue ? 'border-rose-400' : 'border-blue-200'}`}
                data-testid={`followup-${item.id}`}>
                <Button size="icon" variant="ghost" onClick={() => toggleDone(item)}
                  title={item.status === 'done' ? 'Reopen' : 'Mark done'}
                  data-testid={`followup-toggle-${item.id}`}>
                  <Check className={`h-4 w-4 ${item.status === 'done' ? 'text-emerald-600' : 'text-slate-400'}`} />
                </Button>
                <span className={item.status === 'done' ? 'line-through' : 'font-medium'}>{item.note}</span>
                {item.due_date && (
                  <Badge className={item.is_overdue ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'}>
                    due {item.due_date}{item.is_overdue ? ` · ${Math.abs(item.days_to_due)}d late` : ''}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{item.assignee_name}</span>
                <Button size="icon" variant="ghost" className="ml-auto" onClick={() => removeFollowup(item)}
                  data-testid={`followup-delete-${item.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InquiryPeoplePanel;
